import React, { useState, useContext, useEffect, useRef } from 'react'
import { graphql } from 'gatsby'
import ReactMarkdown from 'react-markdown'
import { useQuery, gql } from '@apollo/client'

import { CartContext } from '../context/CartContext'
import { formatPrice } from '../helpers/currency'
import { findSalePrice } from '../helpers/findSalePrice';
import { GTViewItem } from '../helpers/gtagEvents'

import Accordion from '../components/Accordion/Accordion'
import ProductColour from '../components/ProductColour/ProductColour'
import ProductImages from '../components/ProductImages/ProductImages'
import RelatedProducts from "../components/RelatedProducts/RelatedProducts"
import SEO from '../components/SEO/Seo'

const ProductTemplate = ({ data: { strapi: { product, sizeGuide, deliveryAndReturn, sales }}, location }) => {
	const isInitialMount = useRef( true )
	const { addToCart } = useContext( CartContext );
	const [ quantity, setQuantity ] = useState( 1 );
	const [ productPublished, setProductPublished ] = useState( true )
	const [ variationId, setVariationId ] = useState( product.variationsAndQuantities.length ? product.variationsAndQuantities[0].id : null );
	const filterID = { id: product.id }
	let variables = { where: filterID }

	const metaDescription = product.description ? product.description.replace(/(\r\n|\n|\r)/gm, " ").substring(0, 170) : null
	const metaImage = product.images ? {
		src: product.images[0].imageFile.childImageSharp.fluid.src,
		height: 500,
		width: 500,
	} : null

	findSalePrice( product, sales )

	useEffect( () => {
		GTViewItem( product )
	}, [ product ] )

	// Auto selects the first non sold out size
	useEffect( ()=> {

		if ( !product.variationsAndQuantities.length ) return

		const foundVariation = product.variationsAndQuantities.find( node => node.quantity > 0 )
		if ( foundVariation ) {
			setVariationId( foundVariation.id )
		}
	}, [ product.variationsAndQuantities ] )

	const handleSizeChange = ( e ) => {
		setVariationId( e.target.value )
		setQuantity( 1 )
	}

	const quantityInStock = ( id ) => {
		return product.variationsAndQuantities.find( node => node.id === id ).quantity
	}

	const incrementQuantity = ( increment ) => {
		const incrementedQuantity = quantity + increment;

		if ( incrementedQuantity ) {
			setQuantity( incrementedQuantity );
		}
	}

	//ReactMarkdown accepts custom renderers
	const renderers = {
		table: ({ children }) => {
			const table = React.createElement('table', { className: 'table__table' }, children )
			const tableWrapper = React.createElement('div', { className: 'table__wrapper' }, table )
			const container = React.createElement('div', { className: 'table' }, tableWrapper )
			return container
		}
	}

	const generateProductSchema = product => {
		let schema = {
			"@context": "https://schema.org/",
			"@type": "Product",
			"name": `${ product.name }`,
			"image": `${ process.env.SITE_URL }${ metaImage?.src }`,
			"description": `${ product?.description }`,
			"offers": {
				"@type": "Offer",
				"url": `${ process.env.SITE_URL }${ location.pathname }`,
				"priceCurrency": "GBP",
				"price": `${ product.price }`,
			}
		}

		if ( product?.brand ) {
			schema[ 'brand' ] = {
				"@type": "Brand",
				"name": `${ product.brand.name }`
			}
		}

		if ( quantityInStock( variationId ) ) {
			schema.offers[ 'availability' ] = 'https://schema.org/InStock'
		} else {
			schema.offers[ 'availability' ] = 'https://schema.org/OutOfStock'
		}

		return JSON.stringify( schema )
	}

	const productQuery = gql`
		query productQuery( $where: JSON ) {
			products( where: $where ) {
				variationsAndQuantities {
					id
					quantity
					size {
						id
						size
					}
				}
			}
		}
	`

	let { data } = useQuery( productQuery, {
		variables: {
			...variables
		},
		fetchPolicy: "cache-and-network"
	} )

	useEffect(()=> {
		if ( isInitialMount.current) {
			isInitialMount.current = false;
		} else if ( data?.products?.length ) {
			setProductPublished( true )
		} else {
			setProductPublished( false )
		}
	}, [ data, setProductPublished ])

	return (
		<>
			<SEO
				title={ product.name }
				description={ metaDescription }
				image={ metaImage }
				pathname={ location.pathname }>
			
				<script type="application/ld+json">
					{ generateProductSchema( product ) }
				</script>
			</SEO>

			<div className="wrapper">
				<div className="product-template">
					<div className="product-template__main-section">
						<div className="product-template__image">
							{ product.images && <ProductImages images={ product.images }/> }
						</div>

						<div className="product-template__info">
							<h1 className="product-template__name">{ product.name }</h1>
							{ product.brand && <span className="product-template__brand">by { product.brand.name }</span> }

							<div className="product-template__price-container">
								{ product.salePrice &&
									<span className="product-template__price product-template__price--sale">{ formatPrice( product.salePrice ) }</span>
								}
								<span className={`product-template__price ${ product.salePrice ? "disabled" : ''}`}>{ formatPrice( product.price ) }</span>
							</div>

							{ product.description && <ReactMarkdown source={ product.description } className="product-template__description" /> }

							{ product.colour_option &&
								<div className="product-template__colour-container">
									<ProductColour product={ product } />
								</div>
							}

							{ !!product.variationsAndQuantities.length && product.variationsAndQuantities[ 0 ].size &&
								<>
									<div className="product-template__size-container">
										<span className="product-template__size-label">Size:</span>
										{ product.variationsAndQuantities.map( node => {

											if ( !node.size ) return null

											return (
												<button className={`product-template__size ${ node.quantity ? '' : 'sold-out'} ${variationId === node.id ? 'selected' : ''}`}
													onClick={ handleSizeChange }
													key={ node.id }
													value={ node.id }>
												{ node.size.size }
												</button>
											)
										})}
									</div>

									<div className="product-template__inputs-container">
										<button
											className={`button button--no-margin ${ quantityInStock( variationId ) ? '':'disabled'} ${ !productPublished ? 'disabled': '' }`}
											onClick={ () => addToCart( product, quantity, variationId ) }
											disabled={!productPublished}>
												{ productPublished ? (
													<>Add to Bag</>
												) : (
													<>Out of Stock</>
												) }
										</button>
										<div className="product-template__quantity-container">
											<button className={`product-template__quantity-trigger product-template__quantity-trigger--decrease ${ quantity === 1 || !quantityInStock( variationId ) ? 'disabled' : '' } ${ !productPublished ? 'disabled': '' }`}
												onClick={ () => incrementQuantity( -1 ) }
												disabled={!productPublished}>
												_
											</button>
											<div className="product-template__quantity-input">
												{ quantity }
											</div>
											<button className={`product-template__quantity-trigger ${ quantity === quantityInStock( variationId ) || !quantityInStock( variationId ) ? 'disabled' : '' } ${ !productPublished ? 'disabled': '' }`}
												onClick={ () => incrementQuantity( 1 ) }
												disabled={!productPublished}>
												+
											</button>
										</div>
									</div>
								</>
							}

							<Accordion>
								<div accordionTitle="Details" hideItem={ product.additionalDetails ? false : true }>
									{ product.additionalDetails &&
										<p className="product-template__additional-details">{ product.additionalDetails }</p>
									}
								</div>
								<div accordionTitle="Size Guide" hideItem={ !product.showSizeGuide }>
									{ sizeGuide.sizeGuide &&
										<ReactMarkdown source={ sizeGuide.sizeGuide } renderers={ renderers } className="product-template__size-guide" />
									}
								</div>
								<div accordionTitle="Delivery &amp; Returns">
									{ deliveryAndReturn.details &&
										<ReactMarkdown source={ deliveryAndReturn.details } className="product-template__description" />
									}
								</div>
							</Accordion>

						</div>
					</div>
				</div>

				<RelatedProducts id={ product.id } brandName={ product.brand ? product.brand.name : null } subCategory={ product.sub_category ? product.sub_category.label : null }/> 
			</div>
		</>
	)
}

export default ProductTemplate

export const query = graphql`
	query ProductTemplateQuery($id: ID!) {
		strapi {
			product(id: $id) {
				id
				name
				price
				description
				slug
				includeInSale
				sale {
					discount
					amount
				}
				showSizeGuide
				additionalDetails
				brand {
					name
					id
				}
				colour_option {
					hexValue
					name
					id
					colour_category {
						id
						colour
					}
				}
				relatedProducts {
					products {
						id
						slug
						colour_option {
							hexValue
							id
							name
						}
					}
				}
				sub_category {
					id
					label
				}
				variationsAndQuantities {
					id
					quantity
					size {
						id
						size
					}
				}
				images {
					url
					id
					imageFile {
						childImageSharp {
							fluid(maxWidth: 500, maxHeight: 500) {
								...GatsbyImageSharpFluid_noBase64
							}
						}
					}
				}
			}
			sales( where:{ allProducts: true } ) {
				discount
				amount
			}
			sizeGuide {
				sizeGuide
			}
			deliveryAndReturn {
				details
			}
		}
	}
`